import React, { useMemo, useState } from 'react';
import { Modal, Platform, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Picker } from '@react-native-picker/picker';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';

interface Props {
  /** 'HH:mm' */
  value: string;
  onChange: (value: string) => void;
  /** Passo dos minutos (padrão 5). */
  minuteStep?: number;
  label?: string;
  disabled?: boolean;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Campo de horário 'HH:mm'. Abre um seletor com duas rodas (hora/minuto).
 * Sem dependência de fuso — edita a hora local da grade do prestador.
 */
export function TimeField({ value, onChange, minuteStep = 5, label, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [h, m] = value.split(':').map(Number);
  const [draftH, setDraftH] = useState(h || 0);
  const [draftM, setDraftM] = useState(m || 0);

  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
  const minutes = useMemo(
    () => Array.from({ length: Math.floor(60 / minuteStep) }, (_, i) => i * minuteStep),
    [minuteStep],
  );

  const openPicker = () => {
    setDraftH(h || 0);
    setDraftM(m || 0);
    setOpen(true);
  };

  const confirm = () => {
    onChange(`${pad(draftH)}:${pad(draftM)}`);
    setOpen(false);
  };

  return (
    <>
      <PressableScale
        haptic
        disabled={disabled}
        onPress={openPicker}
        className="flex-row items-center gap-1.5 rounded-full border border-neutral-200 bg-card px-3.5 py-2"
        style={{ borderCurve: 'continuous', opacity: disabled ? 0.5 : 1 }}
        accessibilityLabel={label}
      >
        <Ionicons className="text-neutral-500" name="time-outline" size={15} />
        <ThemedText className="text-sm font-semibold text-neutral-800">{value}</ThemedText>
      </PressableScale>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable
          className="flex-1 justify-end bg-black/40"
          onPress={() => setOpen(false)}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            className="rounded-t-3xl bg-card px-5 pb-8 pt-4"
            style={{ borderCurve: 'continuous' }}
          >
            <View className="mb-2 flex-row items-center justify-between">
              <ThemedText className="text-base font-bold text-neutral-900">
                {label ?? 'Escolha o horário'}
              </ThemedText>
              <PressableScale onPress={confirm} className="rounded-full bg-primary px-4 py-2">
                <ThemedText className="text-sm font-bold text-white">OK</ThemedText>
              </PressableScale>
            </View>
            <View className="flex-row items-center justify-center">
              <Picker
                selectedValue={draftH}
                onValueChange={(v) => setDraftH(Number(v))}
                style={{ flex: 1, ...(Platform.OS === 'android' ? {} : { height: 180 }) }}
                itemStyle={{ fontSize: 20 }}
              >
                {hours.map((v) => (
                  <Picker.Item key={v} label={pad(v)} value={v} />
                ))}
              </Picker>
              <ThemedText className="px-1 text-2xl font-bold text-neutral-400">:</ThemedText>
              <Picker
                selectedValue={draftM}
                onValueChange={(v) => setDraftM(Number(v))}
                style={{ flex: 1, ...(Platform.OS === 'android' ? {} : { height: 180 }) }}
                itemStyle={{ fontSize: 20 }}
              >
                {minutes.map((v) => (
                  <Picker.Item key={v} label={pad(v)} value={v} />
                ))}
              </Picker>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}
